import {
  Comparison,
  ComparisonCriterion,
} from "../components/comparison/APComparison";
import { sortBy } from "./array";
import { accumulatedFibonacci } from "./number";

export const sortByCriteria = <T>(
  items: T[],
  criteriaId: keyof T,
  criteria: ComparisonCriterion<T>
) => {
  const sortable = criteria.sortable;

  if (!sortable) {
    return items;
  }

  const sortFunction = sortable.function;

  return sortFunction
    ? [...items].sort((a, b) => sortFunction(a, b, sortable.direction))
    : sortBy([...items], criteriaId, sortable.direction);
};

export const summariseItem = <T>(
  selectedItem: T,
  items: T[],
  criteria: Partial<Record<keyof T, ComparisonCriterion<T>>>,
  summaryCriteria: (keyof T)[]
) => {
  const summaryMap: Record<"win" | "loss", (keyof T)[]> = {
    win: [],
    loss: [],
  };

  summaryCriteria.forEach((criteriaId) => {
    const criterion = criteria[criteriaId];
    if (!criterion) {
      return null;
    }

    const filteredItems = items.filter((item) => item[criteriaId] !== null);

    if (filteredItems.length === 0) {
      return null;
    }

    const sortedItems = sortByCriteria(filteredItems, criteriaId, criterion);
    const maxValue = sortedItems[0][criteriaId];
    const minValue = sortedItems[sortedItems.length - 1][criteriaId];

    let status: "win" | "loss" | null = null;

    if (selectedItem[criteriaId] === maxValue) {
      status = "win";
    } else if (selectedItem[criteriaId] === minValue) {
      status = "loss";
    }

    if (status) {
      summaryMap[status].push(criteriaId);
    }
  });

  return summaryMap;
};

export const getSummarySortFn = <T>(
  items: T[],
  criteria: Partial<Record<keyof T, ComparisonCriterion<T>>>,
  summaryCriteria: (keyof T)[]
) => {
  return (a: T, b: T, direction: "asc" | "desc") => {
    const summaryA = summariseItem(a, items, criteria, summaryCriteria);
    const summaryB = summariseItem(b, items, criteria, summaryCriteria);
    const winScoreA = getSummaryScore(
      summaryA.win.length,
      summaryA.loss.length
    );
    const winScoreB = getSummaryScore(
      summaryB.win.length,
      summaryB.loss.length
    );

    return direction === "asc" ? winScoreA - winScoreB : winScoreB - winScoreA;
  };
};

export const getSummaryScore = (wins: number, losses: number) => {
  const winScore = accumulatedFibonacci(wins);
  const lossScore = accumulatedFibonacci(losses);

  return winScore - lossScore;
};

export const parseComparisonParams = <TCompItem>(
  item: TCompItem,
  initialPrimaryCriteria: string | undefined,
  defaultInitialPrimaryCriteria: keyof TCompItem,
  selectedCriteria: string[],
  summaryCriteriaKey: keyof TCompItem
) => {
  const parsedInitialPrimaryCriteria =
    initialPrimaryCriteria &&
    Object.hasOwn(item as object, initialPrimaryCriteria)
      ? (initialPrimaryCriteria as keyof TCompItem)
      : defaultInitialPrimaryCriteria;
  const parsedSelectedCriteria = selectedCriteria.filter((c) =>
    Object.hasOwn(item as object, c)
  ) as (keyof TCompItem)[];

  return {
    parsedInitialPrimaryCriteria,
    parsedSelectedCriteria,
    summaryCriteria: [parsedInitialPrimaryCriteria, ...parsedSelectedCriteria].filter((c) => c !== summaryCriteriaKey),
  };
};

export const filterOutSelectedCriteria = <TCompItem>(
  criteria: (keyof TCompItem)[],
  selectedCriteria: (keyof TCompItem)[],
  initialPrimaryCriteria: keyof TCompItem | undefined
) => {
  return criteria.filter(
    (c) => !selectedCriteria.includes(c) && c !== initialPrimaryCriteria
  );
};

export const filterExpandableCriteriaFromComparison = <TCompItem>(
  comparison: Comparison<TCompItem>
) => {
  return comparison.expandableCriteria.map((criteria) => ({
    ...criteria,
    criteria: filterOutSelectedCriteria(
      criteria.criteria,
      comparison.selectedCriteria,
      comparison.initialPrimaryCriteria
    ),
  }));
};
